import { useEffect, useState } from "react";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";

import { PieceName, VariantName } from "src/api";
import { createTypedEventEmitter } from "src/shared/util";

import {
  PIECE_DEFINITIONS,
  PieceDefinition,
  pieceDefinitionFor
} from "../game/board/worldObjects/pieces/pieceDefinitions";
import {
  DEPOSIT_BASE_DEFINITIONS,
  RESOURCE_DEPOSIT_DEFINITIONS
} from "../game/board/worldObjects/resourceDeposits/resourceDepositDefinitions";
import { LoadableGLTFResource } from "../loader/LoadableGLTFResource";
import { LoadableProtospriteResource } from "../loader/LoadableProtospriteResource";
import {
  FactionModels,
  type PieceVariantKey,
  getPartsOfPieceVariantKey,
  getPieceVariantKey
} from "../render/models/GameModel";
import { UIIconAnimations, UIIconLayers } from "../ui/icons/iconTypes";
import uiIconPrs from "../ui/icons/ui_icons.prs";

class CentralLoader {
  public readonly events = createTypedEventEmitter<{
    fullyLoaded: void;
  }>();

  private primaryFactionResources = new Map<
    PieceVariantKey,
    LoadableGLTFResource
  >();
  private primaryFactionModels = new FactionModels({});
  private modelResourcesByUrl = new Map<string, LoadableGLTFResource>();
  private iconResource = new LoadableProtospriteResource<
    UIIconLayers,
    UIIconAnimations
  >(uiIconPrs);

  stubPrimaryFactionResource(
    pieceName: PieceName,
    variantName: VariantName | null,
    definition: PieceDefinition
  ) {
    const key = getPieceVariantKey(pieceName, variantName);
    this.primaryFactionResources.set(
      key,
      new LoadableGLTFResource(definition.modelUrl)
    );
  }

  getPrimaryFactionResource(
    pieceName: PieceName,
    variantName: VariantName | null
  ): LoadableGLTFResource {
    const resource = this.primaryFactionResources.get(
      getPieceVariantKey(pieceName, variantName)
    );
    if (!resource)
      throw new Error(`No resource for ${pieceName} ${variantName}`);
    return resource;
  }

  preloadModel(url: string) {
    if (this.modelResourcesByUrl.has(url)) return;
    this.modelResourcesByUrl.set(url, new LoadableGLTFResource(url));
  }

  getLoadedModel(url: string): GLTF {
    const resource = this.modelResourcesByUrl.get(url);
    if (!resource) throw new Error(`Model not loaded: ${url}`);
    return resource.getResource();
  }

  getPrimaryFactionModels() {
    return this.primaryFactionModels;
  }

  createIconSprite(iconName: UIIconAnimations) {
    const sprite = this.iconResource.getResource().getSprite();
    sprite.gotoAnimation(iconName);
    sprite.gotoAnimationFrame(0);
    sprite.center();
    return sprite;
  }

  async queueLoadAll(): Promise<void> {
    const loadPromises: Promise<void>[] = [];
    // Load 3D models.
    for (const [key, resource] of this.primaryFactionResources) {
      if (resource.isLoaded()) continue;
      const [pieceName, variantName] = getPartsOfPieceVariantKey(key);
      if (pieceName === null) continue;
      loadPromises.push(
        resource.load().then(() => {
          const def = pieceDefinitionFor(pieceName, variantName ?? undefined);
          this.primaryFactionModels.addGLTF(resource, pieceName, variantName, {
            scale: def?.scale,
            idleAnimationName: def?.defaultAnimationName,
            moveAnimationName: def?.moveAnimationName,
            attackAnimationName: def?.attackAnimationName,
            attackedAnimationName: def?.hurtAnimationName,
            // Kludge.
            deathAnimationName: def?.hurtAnimationName
          });
        })
      );
    }
    for (const resource of this.modelResourcesByUrl.values()) {
      if (resource.isLoaded()) continue;
      loadPromises.push(resource.load());
    }
    // Load icons.
    loadPromises.push(this.iconResource.load());
    this.primaryFactionModels.addIcons(this.iconResource);
    await Promise.all(loadPromises);
    this.events.emit("fullyLoaded");
  }

  checkFullyLoaded(): boolean {
    for (const resource of this.primaryFactionResources.values()) {
      if (!resource.isLoaded()) return false;
    }
    for (const resource of this.modelResourcesByUrl.values()) {
      if (!resource.isLoaded()) return false;
    }
    if (!this.iconResource.isLoaded()) return false;
    return true;
  }
}

export const kCenteralLoader = new CentralLoader();
// TODO: clean this up.
for (const [pieceName, definition] of Object.entries(PIECE_DEFINITIONS)) {
  kCenteralLoader.stubPrimaryFactionResource(
    pieceName as PieceName,
    null,
    definition
  );
}
for (const definition of Object.values(DEPOSIT_BASE_DEFINITIONS)) {
  kCenteralLoader.preloadModel(definition.modelUrl);
}
for (const definition of Object.values(RESOURCE_DEPOSIT_DEFINITIONS)) {
  for (const oreModel of Object.values(definition.oreModels)) {
    kCenteralLoader.preloadModel(oreModel.modelUrl);
  }
}
kCenteralLoader.queueLoadAll();

export function usePrimaryFactionFullyLoaded() {
  const [loaded, setLoaded] = useState<boolean>(() =>
    kCenteralLoader.checkFullyLoaded()
  );
  useEffect(() => {
    const loadCheck = kCenteralLoader.checkFullyLoaded();
    if (loaded !== loadCheck) {
      setLoaded(loadCheck);
      return;
    }
    const onLoad = () => setLoaded(true);
    kCenteralLoader.events.on("fullyLoaded", onLoad);
    return () => {
      kCenteralLoader.events.off("fullyLoaded", onLoad);
    };
  }, [loaded]);
  return loaded;
}
